import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js';
import { API_BASE_URL } from '../api';


ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const Home = () => {
  const [totalMembers, setTotalMembers] = useState(0);
  const [activeMembers, setActiveMembers] = useState(0); // NEW: Only Active count
  const [santhaMaster, setSanthaMaster] = useState([]);
  const [selectedSantha, setSelectedSantha] = useState('');
  const [eventMaster, setEventMaster] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [expenseMaster, setExpenseMaster] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState('');
  const [santhaDetails, setSanthaDetails] = useState({ total: 0, pending: 0 });
  const [eventDetails, setEventDetails] = useState({ total: 0, pending: 0 });
  const [expenseDetails, setExpenseDetails] = useState({ total: 0, pending: 0 });
  const [lifeMemberDetails, setLifeMemberDetails] = useState({ count: 0, totalAmount: 0 });
  const [totalCollection, setTotalCollection] = useState(0);
  const [pendingCollection, setPendingCollection] = useState(0);
  const [aggregateTotals, setAggregateTotals] = useState({ totalAmount: 0, pendingAmount: 0 });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    members: false,
    santhaMaster: false,
    eventMaster: false,
    expenseMaster: false,
    santhaDetails: false,
    eventDetails: false,
    expenseDetails: false,
    lifeMembers: false,
    aggregate: false,
  });
  const [hoverStates, setHoverStates] = useState({});

  // Shared styles
  const colors = {
    primaryTeal: '#c5a059',
    secondaryPurple: '#1c2541',
    successGreen: '#28a745',
    dangerRed: '#dc3545',
    coral: '#c5a059',
    backgroundLight: '#fafafc',
    textDark: '#0a1128',
    textLight: '#fff',
  };

  const shadows = {
    default: '0 4px 6px rgba(0, 0, 0, 0.1)',
    hover: '0 8px 16px rgba(0, 0, 0, 0.2)',
  };

  // Responsive checks
  const isMobile = window.innerWidth <= 768;
  const isSmallScreen = window.innerWidth <= 576;

  // Hover handlers
  const handleMouseEnter = (card) => {
    setHoverStates((prev) => ({ ...prev, [card]: true }));
  };

  const handleMouseLeave = (card) => {
    setHoverStates((prev) => ({ ...prev, [card]: false }));
  };

  // Helper to set loading state
  const setLoadingState = (key, value) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  // Helper to set error state
  const setErrorState = (key, message) => {
    setErrors((prev) => ({ ...prev, [key]: message }));
  };

  // Fetch Aggregate Totals + Active Members Count
  useEffect(() => {
    const fetchAggregateTotals = async () => {
      try {
        setLoadingState('aggregate', true);

        // Fetch Total & Active Members
        const membersResponse = await fetch(`${API_BASE_URL}/member/get-all-members`);
        const membersJson = await membersResponse.json();

        const allMembers = Array.isArray(membersJson.data)
          ? membersJson.data.filter(m => m.isactive !== true)
          : [];

        setTotalMembers(allMembers.length);

        // Count only ACTIVE members
        const activeOnly = allMembers.filter(m => m.isactive === 'Active');
        setActiveMembers(activeOnly.length);

        // Fetch Santha Data
        const santhaResponse = await fetch(`${API_BASE_URL}/member/getsantha`);
        const santhaJson = await santhaResponse.json();
        const santhaData = Array.isArray(santhaJson.data) ? santhaJson.data : [];

        let santhaTotal = 0;
        let santhaPaid = 0;
        santhaData.forEach((record) => {
          const yearSub = parseFloat(record.yearsub) || 0;
          const meetingFee = parseFloat(record.meetingfee) || 0;
          const tobeCollect = parseFloat(record.tobeCollect) || 8000;
          const memberTotal = yearSub + meetingFee;

          santhaTotal += tobeCollect;
          if (record.status === 'paid') {
            santhaPaid += memberTotal;
          }
        });

        const santhaPending = santhaTotal - santhaPaid;
        setSanthaDetails({ total: santhaPaid, pending: santhaPending });

        // Fetch Event Transaction Data
        const transactionResponse = await fetch(`${API_BASE_URL}/transaction/allTransaction`);
        const transactionJson = await transactionResponse.json();
        const transactionData = Array.isArray(transactionJson.data) ? transactionJson.data : [];
        const transactionTotal = transactionData.reduce((sum, item) => {
          return sum + (parseFloat(item.donateProd) || 0);
        }, 0);
        const transactionPending = transactionData.reduce((sum, item) => {
          return item.status === 'pending' ? sum + (parseFloat(item.donateProd) || 0) : sum;
        }, 0);

        // Fetch Expense Data
        const expenseResponse = await fetch(`${API_BASE_URL}/member/getET`);
        const expenseJson = await expenseResponse.json();
        const expenseData = Array.isArray(expenseJson.data) ? expenseJson.data : [];
        const expenseTotal = expenseData.reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0);
        const expensePending = expenseTotal;

        // Aggregate
        const totalAmount = santhaPaid + transactionTotal + expenseTotal;
        const pendingAmount = santhaPending + transactionPending + expensePending;
        setAggregateTotals({ totalAmount, pendingAmount });
        setErrorState('aggregate', null);
      } catch (error) {
        console.error('Error fetching aggregate totals:', error);
        setErrorState('aggregate', 'Failed to fetch aggregate totals');
      } finally {
        setLoadingState('aggregate', false);
      }
    };

    fetchAggregateTotals();
  }, []);

  // Fetch Santha Master
  useEffect(() => {
    setLoadingState('santhaMaster', true);
    fetch(`${API_BASE_URL}/member/getsanthamaster`)
      .then((response) => response.json())
      .then((data) => {
        const santhaData = Array.isArray(data.data) ? data.data : [];
        setSanthaMaster(santhaData);
        setErrorState('santhaMaster', null);
      })
      .catch((error) => {
        console.error('Error fetching santha master:', error);
        setErrorState('santhaMaster', 'Failed to fetch santha master');
      })
      .finally(() => setLoadingState('santhaMaster', false));
  }, []);

  // Fetch Event Master
  useEffect(() => {
    setLoadingState('eventMaster', true);
    fetch(`${API_BASE_URL}/event`)
      .then((response) => response.json())
      .then((data) => {
        const eventData = Array.isArray(data.data) ? data.data : [];
        setEventMaster(eventData);
        setErrorState('eventMaster', null);
      })
      .catch((error) => {
        console.error('Error fetching event master:', error);
        setErrorState('eventMaster', 'Failed to fetch event master');
      })
      .finally(() => setLoadingState('eventMaster', false));
  }, []);

  // Fetch Expense Master
  useEffect(() => {
    setLoadingState('expenseMaster', true);
    fetch(`${API_BASE_URL}/member/getexpense`)
      .then((response) => response.json())
      .then((data) => {
        const expenseData = Array.isArray(data.data) ? data.data : [];
        setExpenseMaster(expenseData);
        setErrorState('expenseMaster', null);
      })
      .catch((error) => {
        console.error('Error fetching expense master:', error);
        setErrorState('expenseMaster', 'Failed to fetch expense master');
      })
      .finally(() => setLoadingState('expenseMaster', false));
  }, []);

  // Fetch Life Members
  useEffect(() => {
    setLoadingState('lifeMembers', true);
    fetch(`${API_BASE_URL}/member/getlivemember`)
      .then((response) => response.json())
      .then((data) => {
        const lifeMemberData = Array.isArray(data.data) ? data.data : [];
        const count = lifeMemberData.length;
        const totalAmount = lifeMemberData.reduce((sum, item) => {
          return sum + (parseFloat(item.Amount) || 0);
        }, 0);
        setLifeMemberDetails({ count, totalAmount });
        setErrorState('lifeMembers', null);
      })
      .catch((error) => {
        console.error('Error fetching life members:', error);
        setErrorState('lifeMembers', 'Failed to fetch life members');
      })
      .finally(() => setLoadingState('lifeMembers', false));
  }, []);

  // Fetch Santha Details
  useEffect(() => {
    if (selectedSantha) {
      setLoadingState('santhaDetails', true);
      fetch(`${API_BASE_URL}/member/getsantha?santhaId=${selectedSantha}`)
        .then((response) => response.json())
        .then((data) => {
          const santhaData = Array.isArray(data.data) ? data.data : [];
          let total = 0;
          let pending = 0;

          santhaData.forEach((item) => {
            if (item.santhaname === selectedSantha) {
              const yearSub = parseFloat(item.yearsub) || 0;
              const meetingFee = parseFloat(item.meetingfee) || 0;
              const tobeCollect = parseFloat(item.tobeCollect) || 8000;
              const memberTotal = yearSub + meetingFee;

              total += memberTotal;
              if (!item.status || item.status !== 'paid') {
                pending += tobeCollect - memberTotal;
              }
            }
          });

          setSanthaDetails({ total, pending });
          setTotalCollection(total);
          setPendingCollection(pending);
          setErrorState('santhaDetails', null);
        })
        .catch((error) => {
          console.error('Error fetching santha details:', error);
          setErrorState('santhaDetails', 'Failed to fetch santha details');
        })
        .finally(() => setLoadingState('santhaDetails', false));
    } else {
      setSanthaDetails({ total: 0, pending: 0 });
      setTotalCollection(0);
      setPendingCollection(0);
    }
  }, [selectedSantha]);

  // Fetch Event Details
  useEffect(() => {
    if (selectedEvent) {
      setLoadingState('eventDetails', true);
      setEventDetails({ total: 0, pending: 0 });
      const selectedEventData = eventMaster.find((event) => event._id === selectedEvent);
      const expectedEventName = selectedEventData ? selectedEventData.eventName : 'Unknown';
      fetch(`${API_BASE_URL}/transaction/allTransaction?eventId=${selectedEvent}`)
        .then((response) => response.json())
        .then((data) => {
          const eventData = Array.isArray(data.data) ? data.data : [];
          const responseEventName = eventData.length > 0 ? eventData[0].eventName : null;
          if (responseEventName && responseEventName !== expectedEventName) {
            setErrorState('eventDetails', `Data mismatch for ${expectedEventName}`);
            setEventDetails({ total: 0, pending: 0 });
            return;
          }
          const total = eventData.reduce((sum, item) => {
            return sum + (parseFloat(item.donateProd) || 0);
          }, 0);
          const pending = eventData.reduce((sum, item) => {
            return item.status === 'pending' ? sum + (parseFloat(item.donateProd) || 0) : sum;
          }, 0);
          setEventDetails({ total, pending });
          if (eventData.length === 0 || total === 0) {
            setErrorState('eventDetails', `No transactions found for ${expectedEventName}`);
          } else {
            setErrorState('eventDetails', null);
          }
        })
        .catch((error) => {
          console.error('Error fetching event details:', error);
          setErrorState('eventDetails', 'Failed to fetch event details');
        })
        .finally(() => setLoadingState('eventDetails', false));
    } else {
      setEventDetails({ total: 0, pending: 0 });
      setErrorState('eventDetails', null);
    }
  }, [selectedEvent, eventMaster]);

  // Fetch Expense Details
  useEffect(() => {
    if (selectedExpense) {
      setLoadingState('expenseDetails', true);
      fetch(`${API_BASE_URL}/member/getET?expenseId=${selectedExpense}`)
        .then((response) => response.json())
        .then((data) => {
          const expenseData = Array.isArray(data.data) ? data.data : [];
          const total = expenseData.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
          }, 0);
          const pending = total;
          setExpenseDetails({ total, pending });
          setErrorState('expenseDetails', null);
        })
        .catch((error) => {
          console.error('Error fetching expense details:', error);
          setErrorState('expenseDetails', 'Failed to fetch expense details');
        })
        .finally(() => setLoadingState('expenseDetails', false));
    } else {
      setExpenseDetails({ total: 0, pending: 0 });
    }
  }, [selectedExpense]);

  return (
    <div
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: colors.backgroundLight,
        padding: isMobile ? '16px' : '24px',
        minHeight: '100vh',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <i
          className="fas fa-home"
          style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            color: colors.primaryTeal,
            marginRight: '12px',
          }}
        ></i>
        <h1
          style={{
            fontSize: isMobile ? '1.5rem' : '1.8rem',
            fontWeight: 700,
            color: colors.textDark,
            margin: 0,
          }}
        >
          ACCI Dashboard
        </h1>
      </div>

      {errors.members && (
        <div
          className="alert alert-danger alert-dismissible"
          style={{
            borderRadius: '8px',
            padding: '12px',
            fontSize: '1rem',
          }}
        >
          {errors.members}
        </div>
      )}

      <div className="row g-4">

        {/* ========== ACTIVE MEMBERS CARD ========== */}
        <div className="col-md-3">
          <div
            className="card"
            data-testid="active-members"
            onMouseEnter={() => handleMouseEnter('members')}
            onMouseLeave={() => handleMouseLeave('members')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #fbf8f0, #f3ede2)',
              transform: hoverStates.members ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.members ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  gap: isSmallScreen ? '8px' : '0',
                }}
              >
                <i
                  className="fas fa-users"
                  style={{
                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                    color: colors.primaryTeal,
                    marginRight: isSmallScreen ? '0' : '12px',
                  }}
                ></i>
                <h5
                  style={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: colors.textDark,
                  }}
                >
                  Active Members
                </h5>
              </div>

              {loading.aggregate ? (
                <p
                  style={{
                    fontSize: '1rem',
                    color: colors.textDark,
                    textAlign: 'center',
                    margin: '16px 0',
                  }}
                >
                  Loading...
                </p>
              ) : (
                <p
                  style={{
                    fontSize: isMobile ? '2rem' : '2.5rem',
                    fontWeight: 600,
                    color: colors.textDark,
                    textAlign: 'center',
                    margin: 0,
                  }}
                >
                  {activeMembers}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Santha Life Member Card */}
        <div className="col-md-3">
          <div
            className="card"
            data-testid="life-members"
            onMouseEnter={() => handleMouseEnter('lifeMembers')}
            onMouseLeave={() => handleMouseLeave('lifeMembers')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
              transform: hoverStates.lifeMembers ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.lifeMembers ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  gap: isSmallScreen ? '8px' : '0',
                }}
              >
                <i
                  className="fas fa-star"
                  style={{
                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                    color: colors.primaryTeal,
                    marginRight: isSmallScreen ? '0' : '12px',
                  }}
                ></i>
                <h5
                  style={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: colors.textDark,
                  }}
                >
                  Santha Life Members
                </h5>
              </div>
              {loading.lifeMembers ? (
                <p
                  style={{
                    fontSize: '1rem',
                    color: colors.textDark,
                    textAlign: 'center',
                    margin: '16px 0',
                  }}
                >
                  Loading...
                </p>
              ) : errors.lifeMembers ? (
                <p
                  style={{
                    fontSize: '0.9rem',
                    color: colors.dangerRed,
                    margin: '12px 0',
                    textAlign: 'center',
                  }}
                >
                  {errors.lifeMembers}
                </p>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '20px',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        margin: 0,
                        color: colors.textDark,
                      }}
                    >
                      Member Count
                    </p>
                    <p
                      style={{
                        fontSize: isMobile ? '1.25rem' : '1.5rem',
                        fontWeight: 600,
                        margin: 0,
                        color: colors.textDark,
                      }}
                    >
                      {lifeMemberDetails.count}
                    </p>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        margin: 0,
                        color: colors.textDark,
                      }}
                    >
                      Total Amount
                    </p>
                    <p
                      style={{
                        fontSize: isMobile ? '1.25rem' : '1.5rem',
                        fontWeight: 600,
                        margin: 0,
                        color: colors.textDark,
                      }}
                    >
                      ₹{lifeMemberDetails.totalAmount.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Santha Collection Card */}
        <div className="col-md-3">
          <div
            className="card"
            data-testid="santha-collection"
            onMouseEnter={() => handleMouseEnter('santha')}
            onMouseLeave={() => handleMouseLeave('santha')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #fdfbf7, #f7f1e3)',
              transform: hoverStates.santha ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.santha ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  gap: isSmallScreen ? '8px' : '0',
                }}
              >
                <i
                  className="fas fa-money-bill-wave"
                  style={{
                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                    color: colors.successGreen,
                    marginRight: isSmallScreen ? '0' : '12px',
                  }}
                ></i>
                <h5
                  style={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: colors.textDark,
                  }}
                >
                  Santha Collection
                </h5>
              </div>
              {errors.santhaMaster && (
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: colors.dangerRed,
                    margin: '12px 0',
                    textAlign: 'center',
                  }}
                >
                  {errors.santhaMaster}
                </div>
              )}
              <select
                className="form-select"
                value={selectedSantha}
                onChange={(e) => {
                  setSelectedSantha(e.target.value);
                  if (e.target.value) {
                    setSelectedEvent('');
                    setSelectedExpense('');
                  }
                }}
                disabled={loading.santhaMaster}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #ced4da',
                  padding: '10px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  margin: '12px 0',
                }}
              >
                <option value="">Select Santha</option>
                {santhaMaster.length > 0 ? (
                  santhaMaster.map((santha) => (
                    <option key={santha._id} value={santha._id}>
                      {santha.santhaname || 'Unnamed Santha'}
                    </option>
                  ))
                ) : (
                  <option value="">No Santha Available</option>
                )}
              </select>
              {selectedSantha && (
                <div style={{ marginTop: '12px' }}>
                  {loading.santhaDetails ? (
                    <p
                      style={{
                        fontSize: '1rem',
                        color: colors.textDark,
                        textAlign: 'center',
                        margin: '16px 0',
                      }}
                    >
                      Loading...
                    </p>
                  ) : errors.santhaDetails ? (
                    <p
                      style={{
                        fontSize: '0.9rem',
                        color: colors.dangerRed,
                        margin: '12px 0',
                        textAlign: 'center',
                      }}
                    >
                      {errors.santhaDetails}
                    </p>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexDirection: isMobile ? 'column' : 'row',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-wallet"
                          style={{ fontSize: '1.5rem', color: colors.successGreen }}
                        ></i>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>Collected</p>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>₹{santhaDetails.total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-exclamation-circle"
                          style={{ fontSize: '1.5rem', color: colors.dangerRed }}
                        ></i>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>Pending</p>
                          <p style={{ margin: 0, fontWeight: 'bold', color: colors.dangerRed }}>₹{santhaDetails.pending.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event Transaction Card */}
        <div className="col-md-3">
          <div
            className="card"
            data-testid="event-transaction"
            onMouseEnter={() => handleMouseEnter('event')}
            onMouseLeave={() => handleMouseLeave('event')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #f8fafc, #cbd5e1)',
              transform: hoverStates.event ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.event ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  gap: isSmallScreen ? '8px' : '0',
                }}
              >
                <i
                  className="fas fa-calendar-alt"
                  style={{
                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                    color: colors.secondaryPurple,
                    marginRight: isSmallScreen ? '0' : '12px',
                  }}
                ></i>
                <h5
                  style={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: colors.textDark,
                  }}
                >
                  Event Transaction
                </h5>
              </div>
              {errors.eventMaster && (
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: colors.dangerRed,
                    margin: '12px 0',
                    textAlign: 'center',
                  }}
                >
                  {errors.eventMaster}
                </div>
              )}
              <select
                className="form-select"
                value={selectedEvent}
                onChange={(e) => {
                  setSelectedEvent(e.target.value);
                  if (e.target.value) {
                    setSelectedSantha('');
                    setSelectedExpense('');
                  }
                }}
                disabled={loading.eventMaster}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #ced4da',
                  padding: '10px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  margin: '12px 0',
                }}
              >
                <option value="">Select Event</option>
                {eventMaster.length > 0 ? (
                  eventMaster.map((event) => (
                    <option key={event._id} value={event._id}>
                      {event.eventName || 'Unnamed Event'}
                    </option>
                  ))
                ) : (
                  <option value="">No Events Available</option>
                )}
              </select>
              {selectedEvent && (
                <div style={{ marginTop: '12px' }}>
                  {loading.eventDetails ? (
                    <p
                      style={{
                        fontSize: '1rem',
                        color: colors.textDark,
                        textAlign: 'center',
                        margin: '16px 0',
                      }}
                    >
                      Loading...
                    </p>
                  ) : errors.eventDetails ? (
                    <p
                      style={{
                        fontSize: '0.9rem',
                        color: colors.dangerRed,
                        margin: '12px 0',
                        textAlign: 'center',
                      }}
                    >
                      {errors.eventDetails}
                    </p>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexDirection: isMobile ? 'column' : 'row',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-wallet"
                          style={{ fontSize: '1.5rem', color: colors.successGreen }}
                        ></i>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>Collected</p>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>₹{eventDetails.total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-exclamation-circle"
                          style={{ fontSize: '1.5rem', color: colors.dangerRed }}
                        ></i>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>Pending</p>
                          <p style={{ margin: 0, fontWeight: 'bold', color: colors.dangerRed }}>₹{eventDetails.pending.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expense Card */}
        <div className="col-md-3">
          <div
            className="card"
            data-testid="expense"
            onMouseEnter={() => handleMouseEnter('expense')}
            onMouseLeave={() => handleMouseLeave('expense')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #fff5f5, #fed7d7)',
              transform: hoverStates.expense ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.expense ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  gap: isSmallScreen ? '8px' : '0',
                }}
              >
                <i
                  className="fas fa-credit-card"
                  style={{
                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                    color: colors.dangerRed,
                    marginRight: isSmallScreen ? '0' : '12px',
                  }}
                ></i>
                <h5
                  style={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: colors.textDark,
                  }}
                >
                  Expense
                </h5>
              </div>
              {errors.expenseMaster && (
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: colors.dangerRed,
                    margin: '12px 0',
                    textAlign: 'center',
                  }}
                >
                  {errors.expenseMaster}
                </div>
              )}
              <select
                className="form-select"
                value={selectedExpense}
                onChange={(e) => {
                  setSelectedExpense(e.target.value);
                  if (e.target.value) {
                    setSelectedSantha('');
                    setSelectedEvent('');
                  }
                }}
                disabled={loading.expenseMaster}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #ced4da',
                  padding: '10px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  margin: '12px 0',
                }}
              >
                <option value="">Select Expense</option>
                {expenseMaster.length > 0 ? (
                  expenseMaster.map((expense) => (
                    <option key={expense._id} value={expense._id}>
                      {expense.expensetype || 'Unnamed Expense'}
                    </option>
                  ))
                ) : (
                  <option value="">No Expenses Available</option>
                )}
              </select>
              {selectedExpense && (
                <div style={{ marginTop: '12px' }}>
                  {loading.expenseDetails ? (
                    <p
                      style={{
                        fontSize: '1rem',
                        color: colors.textDark,
                        textAlign: 'center',
                        margin: '16px 0',
                      }}
                    >
                      Loading...
                    </p>
                  ) : errors.expenseDetails ? (
                    <p
                      style={{
                        fontSize: '0.9rem',
                        color: colors.dangerRed,
                        margin: '12px 0',
                        textAlign: 'center',
                      }}
                    >
                      {errors.expenseDetails}
                    </p>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexDirection: isMobile ? 'column' : 'row',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-wallet"
                          style={{ fontSize: '1.5rem', color: colors.dangerRed }}
                        ></i>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#555' }}>Total Expense</p>
                          <p style={{ margin: 0, fontWeight: 'bold', color: colors.dangerRed }}>₹{expenseDetails.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Summary Card */}
        <div className="col-12" style={{ marginTop: '35px' }}>
          <div
            className="card"
            data-testid="financial-summary"
            onMouseEnter={() => handleMouseEnter('financial')}
            onMouseLeave={() => handleMouseLeave('financial')}
            style={{
              border: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #0a1128, #1c2541)',
              color: colors.textLight,
              transform: hoverStates.financial ? 'scale(1.02)' : 'scale(1)',
              boxShadow: hoverStates.financial ? shadows.hover : shadows.default,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <div style={{ padding: '20px' }}>
              <h5
                style={{
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  fontWeight: 600,
                  margin: '0 0 16px',
                  color: colors.textLight,
                }}
              >
                Financial Summary
              </h5>
              {loading.santhaDetails || loading.eventDetails || loading.expenseDetails ? (
                <p
                  style={{
                    fontSize: '1rem',
                    color: colors.textLight,
                    textAlign: 'center',
                    margin: '16px 0',
                  }}
                >
                  Loading...
                </p>
              ) : errors.santhaDetails || errors.eventDetails || errors.expenseDetails ? (
                <p
                  style={{
                    fontSize: '0.9rem',
                    color: colors.dangerRed,
                    margin: '12px 0',
                    textAlign: 'center',
                  }}
                >
                  {errors.santhaDetails || errors.eventDetails || errors.expenseDetails}
                </p>
              ) : selectedSantha || selectedEvent || selectedExpense ? (
                (() => {
                  let firstLabel = '';
                  let firstVal = '';
                  let secondLabel = '';
                  let secondVal = '';
                  let thirdLabel = '';
                  let thirdVal = '';
                  let showThird = false;

                  if (selectedSantha) {
                    firstLabel = 'Santha Target';
                    firstVal = `₹${(totalMembers * 8000).toFixed(2)}`;
                    secondLabel = 'Collected Amount';
                    secondVal = `₹${santhaDetails.total.toFixed(2)}`;
                    thirdLabel = 'Pending Amount';
                    thirdVal = `₹${santhaDetails.pending.toFixed(2)}`;
                    showThird = true;
                  } else if (selectedEvent) {
                    const selectedEventData = eventMaster.find((event) => event._id === selectedEvent);
                    firstLabel = 'Selected Event';
                    firstVal = selectedEventData ? selectedEventData.eventName : 'Event';
                    secondLabel = 'Collected Amount';
                    secondVal = `₹${eventDetails.total.toFixed(2)}`;
                    thirdLabel = 'Pending Amount';
                    thirdVal = `₹${eventDetails.pending.toFixed(2)}`;
                    showThird = true;
                  } else if (selectedExpense) {
                    const selectedExpenseData = expenseMaster.find((expense) => expense._id === selectedExpense);
                    firstLabel = 'Selected Expense';
                    firstVal = selectedExpenseData ? selectedExpenseData.expensetype : 'Expense';
                    secondLabel = 'Total Expense';
                    secondVal = `₹${expenseDetails.total.toFixed(2)}`;
                    showThird = false;
                  }

                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexDirection: isMobile ? 'column' : 'row',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-info-circle"
                          style={{ fontSize: '1.5rem', color: colors.textLight }}
                        ></i>
                        <div>
                          <p
                            style={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              margin: 0,
                              opacity: 0.9,
                            }}
                          >
                            {firstLabel}
                          </p>
                          <p
                            style={{
                              fontSize: isMobile ? '1.25rem' : '1.5rem',
                              fontWeight: 600,
                              margin: 0,
                            }}
                          >
                            {firstVal}
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <i
                          className="fas fa-wallet"
                          style={{ fontSize: '1.5rem', color: colors.textLight }}
                        ></i>
                        <div>
                          <p
                            style={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              margin: 0,
                              opacity: 0.9,
                            }}
                          >
                            {secondLabel}
                          </p>
                          <p
                            style={{
                              fontSize: isMobile ? '1.25rem' : '1.5rem',
                              fontWeight: 600,
                              margin: 0,
                            }}
                          >
                            {secondVal}
                          </p>
                        </div>
                      </div>

                      {showThird && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <i
                            className="fas fa-exclamation-circle"
                            style={{ fontSize: '1.5rem', color: colors.dangerRed }}
                          ></i>
                          <div>
                            <p
                              style={{
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                margin: 0,
                                opacity: 0.9,
                              }}
                            >
                              {thirdLabel}
                            </p>
                            <p
                              style={{
                                fontSize: isMobile ? '1.25rem' : '1.5rem',
                                fontWeight: 600,
                                margin: 0,
                                color: colors.dangerRed,
                              }}
                            >
                              {thirdVal}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <p
                  style={{
                    fontSize: '1rem',
                    color: colors.textLight,
                    textAlign: 'center',
                    margin: '16px 0',
                  }}
                >
                  Select a Santha, Event, or Expense to view summary
                </p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;